import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { orderBy } from 'lodash-es';
import { BehaviorSubject, combineLatest, map, Observable } from 'rxjs';
import { LocalStorageSignalService } from '../local-storage-signal.service';
import { AzureFeatureFlagService } from './azure-feature-flag.service';

export type ReleaseBannerType = 'info' | 'warning';
export type IxReleaseBannerDict = { [key in ReleaseBannerType]: IxReleaseBanner };

interface IxReleaseBannerBase {
  id: string;
  version: string;
  type: ReleaseBannerType;
  dismissible: boolean;
  releaseDate: string;
  active: boolean;
  lastModified: Date;
}

export interface IxReleaseInfoBanner extends IxReleaseBannerBase {
  releaseNotesUrl: string;
}

export interface IxReleaseWarningBanner extends IxReleaseBannerBase {
  startsAtUtc: string;
  duration: number;
}

export type IxReleaseBanner = IxReleaseInfoBanner | IxReleaseWarningBanner;

type IxReleaseBannerDismissStatusMap = Record<string, boolean>;

// Persists which banners a user has already dismissed so they stay hidden across reloads.
const LS_IX_RELEASE_BANNER_DISMISS_STATUS = 'IX_RELEASE_BANNER_DISMISS_STATUS';

@Injectable({
  providedIn: 'root',
})
export class ReleaseBannerService {
  readonly #azFeatureFlagService = inject(AzureFeatureFlagService);
  readonly #localStorageSignalService = inject(LocalStorageSignalService);
  // Local storage changes are not observable by themselves, so this forces the stream to re-evaluate.
  readonly #dismissChange$ = new BehaviorSubject(0);
  // Build the current candidate list from azure config values, then remove inactive and dismissed banners.
  readonly #bannersToShow$: Observable<IxReleaseBanner[]> = combineLatest([
    this.#azFeatureFlagService.allReleaseBanners$,
    this.#dismissChange$,
  ]).pipe(
    map(([releaseBannerDict]) => Object.values(releaseBannerDict)),
    map(banners => this.#filterActiveBanners(banners)),
    map(banners => this.#filterDismissedBanners(banners)),
  );
  readonly bannerToShow = toSignal(
    this.#bannersToShow$.pipe(map(banners => this.#selectBannerToShow(banners))),
    { initialValue: null },
  );
  markBannerDismissed(banner: IxReleaseBanner): void {
    this.#setDismissStatus(banner, true);
    this.#dismissChange$.next(this.#dismissChange$.value + 1);
  }

  #filterActiveBanners(banners: IxReleaseBanner[]): IxReleaseBanner[] {
    return banners.filter(banner => banner.active);
  }

  #filterDismissedBanners(banners: IxReleaseBanner[]): IxReleaseBanner[] {
    const dismissStatusMap = this.#getDismissStatusMap();
    return banners.filter(banner => !dismissStatusMap[getBannerSignature(banner.id, banner.lastModified)]);
  }

  #selectBannerToShow(banners: IxReleaseBanner[]): IxReleaseBanner | null {
    if (banners.length < 1) {
      return null;
    }

    // When more than one banner qualifies, prefer the most recently updated one.
    const sortedBanners = orderBy(banners, banner => this.#toTimestamp(banner.lastModified) ?? 0, 'desc');
    return sortedBanners[0] ?? null;
  }

  #getDismissStatusMap(): IxReleaseBannerDismissStatusMap {
    return (
      this.#localStorageSignalService.get<IxReleaseBannerDismissStatusMap>(
        LS_IX_RELEASE_BANNER_DISMISS_STATUS,
      ) ?? {}
    );
  }

  #setDismissStatus(banner: IxReleaseBanner, dismissed: boolean): void {
    const dismissStatusMap = this.#getDismissStatusMap();
    this.#localStorageSignalService.set<IxReleaseBannerDismissStatusMap>(
      LS_IX_RELEASE_BANNER_DISMISS_STATUS,
      {
        ...dismissStatusMap,
        [getBannerSignature(banner.id, banner.lastModified)]: dismissed,
      },
    );
  }

  #toTimestamp(dateValue: string | Date): number | null {
    const timestamp = new Date(dateValue).getTime();
    return Number.isNaN(timestamp) ? null : timestamp;
  }
}

export function getBannerSignature(bannerId: string, lastModified: Date): string {
  // Generate a unique signature for the banner based on its ID and last modified date.
  return `${bannerId}:${new Date(lastModified).toISOString()}`;
}
